/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  Chip,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { Alert, AlertTitle } from '@material-ui/lab';
import Button from 'components/Button';
import { DialogAlertProps } from 'components/DialogAlert';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  ListObjectOperatorDataProps as ListObjectProps,
  ObjectOperatorProps as ObjectProps,
} from 'modules/permission/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState, { ListDataProps } from 'store/interfaces';
import FormOperator from 'containers/Operators/Form';
import { initialEdit } from 'containers/Operators/Form/initialValues';
import { newOperatorAtGroupRequest } from 'modules/operators/actions';
import { GetOperatorFields } from 'modules/operators/interfaces';
import Loading from 'components/Loading';
import { useTableFilters } from 'hooks/useTableFilters';
import { initialQuery, mapAllSelected } from '..';
import TemplateGroups from '../../Template';
import styles from '../../Template/styles';
import ActionButtons from '../ActionButtons';
import ListAvailable from './ListAvailable';
import ListPermissions from './ListPermissions';
import { handleObjectsSave } from '../utils/objectsUtils';
import { handlePermissionsSave } from '../utils/permissionsUtils';
import { handleCheck } from '../utils/utils';

const useStyles = makeStyles(styles);

export const ENTITY_INDEX = 4;
export const ENTITY_NAME = 'operator';

interface TemplateProps {
  title: string;
  subtitle: React.ReactNode;
  tabs: {
    items: any[];
    defaultValue: number;
    onChange: (value: number) => void;
  };
}

interface Props {
  id: string;
  hidden?: boolean;
  template: TemplateProps;
  items: {
    loading: boolean;
    selected: ListObjectProps;
    available: ListObjectProps;
  };
  allItemsSelected: boolean;
  permissions: any;
  isLoadingOperator: boolean;
  isLoadingPermission: boolean;
  onLoad: (entity: string, query?: typeof initialQuery) => void;
  onPrevStep?: () => void;
  onNextStep?: () => void;
  onCancel?: () => void;
  onRemove: (entity: string, objects: string[]) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  updateObjects: (entity: string, objects: string[]) => void;
  updatePermissions: (entity: string, permissions: string[]) => void;
  editGroup: () => void;
  newOperatorAtGroupRequest: (values: GetOperatorFields) => void;
}

const Content = ({
  id,
  hidden,
  template,
  items,
  allItemsSelected,
  permissions,
  isLoadingOperator,
  isLoadingPermission,
  onLoad,
  onPrevStep,
  onNextStep,
  onCancel,
  onRemove,
  modalOpen,
  modalClose,
  updateObjects,
  updatePermissions,
  editGroup,
  newOperatorAtGroupRequest,
}: Props) => {
  const classes = useStyles();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setQuery,
  } = useTableFilters(initialQuery);

  const [checked, setChecked] = React.useState<string[]>([]);
  const [alert] = React.useState<DialogAlertProps | null>(null);
  const [data, setData] = React.useState<ListDataProps | null>(null);
  const [permissionItems, setPermissionItems] = React.useState<string[]>([]);
  const [pending, setPending] = React.useState<{
    objects: ObjectProps[];
    permissions: string[];
  }>({
    objects: [],
    permissions: [],
  });

  const currentTab = template.tabs.items[ENTITY_INDEX];

  const handleSubmit = (values: GetOperatorFields) => {
    newOperatorAtGroupRequest(values);
  };

  const handleSave = () => {
    handleObjectsSave(
      pending.objects,
      data?.results,
      allItemsSelected,
      updateObjects,
      currentTab.entity
    );
    handlePermissionsSave(
      pending.permissions,
      permissionItems,
      currentTab.entity,
      updatePermissions
    );
    setPending({ objects: [], permissions: [] });
  };

  const handleAdd = () => {
    modalOpen({
      title: i18n.t('GROUPS_PAGE.ADD_OPERATORS_KEY'),
      subtitle: i18n.t('GROUPS_PAGE.ADD_OPERATORS_SUBTITLE_KEY'),
      content: (
        <ListAvailable
          id={id}
          pending={pending.objects}
          onCancel={modalClose}
          onConfirm={objects => {
            modalClose();
            setPending({ ...pending, objects });
          }}
        />
      ),
      maxWidth: 'md',
      fullWidth: true,
      noBorder: true,
    });
  };
  const handleNewOperator = () => {
    modalOpen({
      title: i18n.t('OPERATORS_PAGE.ADD_OPERATOR_TITLE_KEY'),
      subtitle: i18n.t('OPERATORS_PAGE.ADD_OPERATOR_SUBTITLE_KEY'),
      overrideClasses: {
        contentNoBorder: {
          maxHeight: 600,
        },
      },
      content: (
        <FormOperator
          initialValues={initialEdit}
          onCancel={modalClose}
          isLoading={isLoadingOperator}
          onSubmit={handleSubmit}
          modalVersion
          useCompanyList
        />
      ),
      maxWidth: 'lg',
      fullWidth: true,
      noBorder: true,
    });
  };

  const handleRemove = (ids: string[]) => {
    const item = data?.results.find(({ uuid }) => uuid === ids[ids.length - 1]);
    const { first_name, last_name } = item;
    let titleKey = 'GROUPS_PAGE.WANT_REMOVE_SELECTED_OPERATORS_KEY';
    let contentKey = 'GROUPS_PAGE.ACTION_IS_IRREVERSIBLE_KEY';

    if (ids.length === 1) {
      titleKey = 'GROUPS_PAGE.WANT_REMOVE_OPERATOR_KEY';
      contentKey = 'GROUPS_PAGE.REMOVE_WARNING_MSG_KEY';
    }

    modalOpen({
      title: i18n.t(titleKey),
      content: i18n.t(contentKey, {
        name: `${first_name} ${last_name}`,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              onRemove(currentTab.entity, ids);
              setChecked(prev => prev.filter(uuid => !ids.includes(uuid)));
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  React.useEffect(() => {
    if (!items?.selected) return;
    setData(items.selected);

    currentTab.count = items.selected.count;
  }, [items]);

  React.useEffect(() => {
    if (!hidden) {
      setQuery(initialQuery);
    }
  }, [hidden]);

  React.useEffect(() => {
    if (!hidden) {
      onLoad(currentTab.entity, query);
    }
  }, [query]);

  if (!data) {
    return <Loading />;
  }

  return !hidden ? (
    <React.Fragment>
      <TemplateGroups
        {...template}
        contentTitle={i18n.t('GROUPS_PAGE.LIST_OPERATORS_TITLE_KEY')}
        contentSubtitle={i18n.t('GROUPS_PAGE.LIST_OPERATORS_SUBTITLE_KEY')}
        alert={alert}
        disableArea={permissions[ENTITY_NAME]?.loading}
        generalActions={[
          {
            label: i18n.t('ADD_OPERATOR_KEY'),
            dataCypress: 'add-operator-to-group-button',
            onClick: handleAdd,
          },
          {
            label: i18n.t('CREATE_NEW_OPERATOR'),
            dataCypress: 'create-new-operator-button',
            onClick: handleNewOperator,
          },
          {
            label: i18n.t('REMOVE_SELECTED_OPERATORS_KEY'),
            dataCypress: 'remove-selected-operators-button',
            onClick: () => handleRemove(checked),
            disabled: checked.length === 0,
          },
          {
            label: i18n.t('EDIT_INFO_KEY'),
            dataCypress: 'edit-info-button',
            onClick: editGroup,
          },
        ]}
        onSearch={handleSearch}
        marginSearchTop
        isLoading={items?.loading || isLoadingPermission}
      >
        {pending.objects.length > 0 && (
          <Grid container xs={12} className={classes.pendingList}>
            <Alert severity="warning">
              <AlertTitle>{i18n.t('ITEMS_TO_ADD_KEY')}:</AlertTitle>
              {pending.objects.map(({ uuid, first_name, last_name }) => (
                <Chip
                  key={uuid}
                  label={`${first_name} ${last_name}`}
                  onDelete={() => {
                    setPending({
                      ...pending,
                      objects: pending.objects.filter(
                        item => uuid !== item?.uuid
                      ),
                    });
                  }}
                  variant="outlined"
                />
              ))}
            </Alert>
          </Grid>
        )}
        <TableItems
          overrideRowsCount={10}
          header={
            <>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  onChange={e => handleCheck.toggleAll(e, data, setChecked)}
                />
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('first_name')}>
                {i18n.t('OPERATOR_NAME_KEY')}
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('email')}>
                {i18n.t('EMAIL_KEY')}
              </TableCell>
              <TableCell
                order
                handleOrder={() => handleOrder('company__company_name')}
              >
                {i18n.t('COMPANIES_KEY')}
              </TableCell>
              <TableCell centerTitle>{i18n.t('ACTIONS_KEY')}</TableCell>
            </>
          }
          renderItems={(item: ObjectProps) => (
            <TableRow key={item?.uuid}>
              <TableCell className={classes.checkboxCell} style={{ width: 20 }}>
                <Checkbox
                  onChange={() => handleCheck.toggle(item?.uuid, setChecked)}
                  checked={handleCheck.exists(item?.uuid, checked)}
                />
              </TableCell>
              <TableCell>{`${item?.first_name} ${item?.last_name}`}</TableCell>
              <TableCell>{item?.email}</TableCell>
              <TableCell>{item?.company?.name}</TableCell>
              <TableCell className={classes.deleteIcon}>
                <IconButton onClick={() => handleRemove([item?.uuid])}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          )}
          addButton={
            <>
              <Button
                variant="outlined"
                onClick={handleNewOperator}
                startIcon={<PersonAddIcon />}
                className={classes.createOperator}
                data-cy={'create-new-operator-button'}
              >
                {i18n.t('CREATE_NEW_OPERATOR')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleAdd}
                disabled={allItemsSelected}
                data-cy={'add-operator-to-group-button'}
              >
                {i18n.t('ADD_OPERATOR_KEY')}
              </Button>
            </>
          }
          data={data}
          isLoading={items?.loading && data.count === 0}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={
            <>
              <Typography variant="body1" className={classes.notFountText}>
                {i18n.t('NO_ADDED_OPERATOR_KEY')}
              </Typography>
              <>
                <Button
                  variant="outlined"
                  onClick={handleNewOperator}
                  startIcon={<PersonAddIcon />}
                  className={classes.createOperator}
                  data-cy={'create-new-operator-button'}
                >
                  {i18n.t('CREATE_NEW_OPERATOR')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleAdd}
                  data-cy={'add-operator-to-group-button'}
                >
                  {i18n.t('ADD_OPERATOR_KEY')}
                </Button>
              </>
            </>
          }
          colCount={5}
        />
      </TemplateGroups>

      <ListPermissions
        title={i18n.t('PERMISSIONS_KEY')}
        subtitle={i18n.t('GROUPS_PAGE.LIST_OPERATOR_PERMISSIONS_KEY')}
        pending={pending.permissions}
        setPending={values => setPending({ ...pending, permissions: values })}
        updatePermissions={(values: string[]) => setPermissionItems(values)}
      />

      <ActionButtons
        disableSave={
          pending.objects.length === 0 && pending.permissions.length === 0
        }
        onCancel={onCancel}
        onSave={handleSave}
        onPrevStep={onPrevStep}
        onNextStep={onNextStep}
      />
    </React.Fragment>
  ) : null;
};

const mapStateToProps = ({
  permission: {
    objects,
    roleInfo,
    permissions,
    isLoading: isLoadingPermission,
  },
  operators: { isLoading },
}: ApplicationState) => ({
  items: objects[ENTITY_NAME],
  permissions,
  allItemsSelected: roleInfo[mapAllSelected[ENTITY_NAME]],
  isLoadingOperator: isLoading,
  isLoadingPermission,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  newOperatorAtGroupRequest,
})(Content);
